import is from '@sindresorhus/is';
import type { FunctionComponent, JSX, PropsWithChildren, ReactNode } from 'react';
import { useContext } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { ErrorBoundary } from 'react-error-boundary';

import { ApplicationContext } from '~/shared/context/context';
import { is400Error, is403Error, is404Error, is500Error, isNetworkError } from '~/shared/error-code/error-code';
import type { Translate, TranslationKey } from '~/shared/internationalization/translation';
import type { Logger } from '~/shared/logger/logger';
import { Button } from '~/ui/core/button/Button';
import { IconCrossSmall } from '~/ui/core/icons';
import { TileBase } from '~/ui/core/tile/Tile';
import { useTilingEngine } from '~/ui/domain/tiling-engine/TilingEngineContext';

import { PrimaryActionButton } from '../../design/components/button/PrimaryAction';
import { ErrorAlert } from '../../design/components/errors/ErrorAlert';
import { ErrorPage } from '../../design/components/errors/ErrorPage';
import errorIllustration2x from '../../design/components/errors/error-illustration@2x.png';
import errorIllustration from '../../design/components/errors/error-illustration.png';
import { Error400, Error403, Error404, Error500 } from './ErrorPages';

export const renderErrorCodePage = (translate: Translate): ((props: FallbackProps) => ReactNode) => {
    const ErrorCodePage = ({ error }: FallbackProps): JSX.Element => {
        if (is400Error(error)) {
            return <Error400 />;
        }

        if (is403Error(error)) {
            return <Error403 />;
        }

        if (is404Error(error)) {
            return <Error404 />;
        }

        if (is500Error(error)) {
            return <Error500 />;
        }

        if (isNetworkError(error)) {
            return (
                <ErrorPage message={translate('errors.network.message')} title={translate('errors.network.title')}>
                    <PrimaryActionButton
                        label={translate('errors.common.action')}
                        onClick={() => globalThis.location.reload()}
                    />
                </ErrorPage>
            );
        }

        return (
            <ErrorPage message={translate('errors.common.message')} title={translate('errors.common.title')}>
                <PrimaryActionButton
                    label={translate('errors.common.action')}
                    onClick={() => globalThis.location.reload()}
                />
            </ErrorPage>
        );
    };

    return ErrorCodePage;
};

const onPageErrorMessageBoundaryError = (error: unknown, logger: Logger): void => {
    if (error instanceof Error && error.message.startsWith('Failed to fetch dynamically imported module')) {
        logger.warn('reloading without cache', globalThis.location.href);
        fetch(globalThis.location.href, { cache: 'no-cache' }).then(() => globalThis.location.reload());
    }
};

export const PageErrorBoundary: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const {
        applicationState: { location },
        logger,
        translate,
    } = useContext(ApplicationContext);
    const { pathname } = location;

    return (
        <ErrorBoundary
            fallbackRender={renderErrorCodePage(translate)}
            resetKeys={[pathname]}
            onError={(error: unknown) => {
                onPageErrorMessageBoundaryError(error, logger);
            }}
        >
            {children}
        </ErrorBoundary>
    );
};

export const renderComponentErrorMessage = (message: string): ((props: FallbackProps) => ReactNode) => {
    const Wrapped = (): JSX.Element => <ErrorAlert message={message} />;
    Wrapped.displayName = 'ComponentErrorMessage';

    return Wrapped;
};

type ComponentErrorBoundaryProps = PropsWithChildren<{ readonly message?: TranslationKey }>;

export const ComponentErrorBoundary: FunctionComponent<ComponentErrorBoundaryProps> = ({ children, message }) => {
    const { translate } = useContext(ApplicationContext);
    const translatedErrorMessage = translate(message ?? 'errors.common.message');

    return (
        <ErrorBoundary fallbackRender={renderComponentErrorMessage(translatedErrorMessage)}>{children}</ErrorBoundary>
    );
};

type TileErrorFallbackProps = {
    readonly message: string;
    readonly resetErrorBoundary: () => void;
    readonly tileId?: string;
};

const FEEDBACK_MAILTO = 'mailto:feedback@cobrainer.com?subject=Error Cobrainer App';

const TileErrorFallback: FunctionComponent<TileErrorFallbackProps> = ({ message, resetErrorBoundary, tileId }) => {
    const { translate } = useContext(ApplicationContext);
    const { hasTile, unregisterTile } = useTilingEngine();

    const isRegisteredTile = is.string(tileId) && hasTile(tileId);

    const handleClose = (): void => {
        if (is.string(tileId) && isRegisteredTile) {
            unregisterTile(tileId);
        }

        resetErrorBoundary();
    };

    const handleFeedback = (): void => {
        window.open(FEEDBACK_MAILTO);
    };

    const handleReload = (): void => {
        globalThis.location.reload();
    };

    return (
        <TileBase className="border border-gray-300 p-3">
            <div className="flex w-full shrink-0 items-center">
                <Button
                    aria-label={translate('common.close')}
                    iconOnly={true}
                    size="xs"
                    variant="round"
                    onClick={handleClose}
                >
                    <IconCrossSmall />
                </Button>
            </div>
            <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center">
                <div className="flex min-h-0 w-full max-w-[26rem] flex-1 flex-col gap-10xl">
                    <div className="flex shrink-0 flex-col gap-xs">
                        <h1 className="m-0 text-xl font-normal text-black-900">{translate('errors.common.title')}</h1>
                        <p className="m-0 text-sm text-gray-900">{message}</p>
                    </div>
                    <div className="flex shrink-0 items-start gap-m">
                        <Button size="sm" variant="secondary" onClick={handleFeedback}>
                            Provide feedback
                        </Button>
                        <Button size="sm" variant="default" onClick={handleReload}>
                            Reload
                        </Button>
                    </div>
                    <img
                        alt=""
                        className="min-h-0 w-full max-w-[26rem] flex-1 rounded-3xl object-contain object-left-top shadow-lg"
                        src={errorIllustration}
                        srcSet={`${errorIllustration2x} 2x`}
                    />
                </div>
            </div>
        </TileBase>
    );
};

const renderTileErrorMessage = (message: string, tileId?: string): ((props: FallbackProps) => ReactNode) => {
    const Wrapped = ({ resetErrorBoundary }: FallbackProps): JSX.Element => (
        <TileErrorFallback message={message} resetErrorBoundary={resetErrorBoundary} tileId={tileId} />
    );
    Wrapped.displayName = 'TileErrorMessage';

    return Wrapped;
};

type TileErrorBoundaryProps = PropsWithChildren<{
    readonly message?: TranslationKey;
    readonly tileId?: string;
}>;

export const TileErrorBoundary: FunctionComponent<TileErrorBoundaryProps> = ({ children, message, tileId }) => {
    const { translate } = useContext(ApplicationContext);
    const translatedErrorMessage = translate(message ?? 'errors.common.message');

    return (
        <ErrorBoundary fallbackRender={renderTileErrorMessage(translatedErrorMessage, tileId)}>
            {children}
        </ErrorBoundary>
    );
};
